/**
 * Zapier Setup Guide Component
 * Reusable component for displaying Zapier integration setup instructions
 */

import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

interface ZapierSetupGuideProps {
  webhook: {
    id: string;
    secret_key: string;
    webhook_type: string;
    custom_mapping?: Record<string, string>;
    metadata?: any;
  };
  testData?: any;
}

const ZapierSetupGuide: React.FC<ZapierSetupGuideProps> = ({ webhook, testData }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Get API base URL helper function
  const getApiBaseUrl = (): string => {
    const hostname = window.location.hostname.toLowerCase();
    const defaultApiUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.feedbackgate.com';
    
    // If on app.feedbackgate.com, convert to api.feedbackgate.com
    if (hostname === 'app.feedbackgate.com' || hostname.startsWith('app.')) {
      const protocol = window.location.protocol;
      const apiHost = hostname.replace(/^app\./, 'api.');
      return `${protocol}//${apiHost}`;
    }
    
    // If on custom domain, use same domain for API
    if (hostname && 
        !hostname.includes('feedbackgate.com') && 
        !hostname.includes('localhost') && 
        !hostname.includes('127.0.0.1')) {
      const protocol = window.location.protocol;
      if (hostname.startsWith('www.')) {
        const apiHost = hostname.replace(/^www\./, 'api.');
        return `${protocol}//${apiHost}`;
      } else {
        return `${protocol}//${hostname}/api`;
      }
    }
    
    return defaultApiUrl;
  };

  const webhookUrl = `${getApiBaseUrl()}/api/automation/hooks/zap`;

  const getSetupInstructions = () => {
    if (webhook.webhook_type === 'zapier') {
      return {
        title: "Set up your Zapier integration",
        steps: [
          "Go to Zapier.com and create a new Zap",
          "Choose your trigger app (where your data comes from)",
          "Set up when the Zap should fire",
          "Add 'Webhooks by Zapier' as the action → Select 'POST' as the event type",
          "In the webhook configuration page, configure the following:",
          "  • Payload Type: Select 'Json' from the dropdown (required!)",
          "  • URL: Paste your webhook URL (shown below)",
          "  • Data: Add your contact fields using the key-value method (see detailed guide below)",
          "  • Headers: Add X-Webhook-Secret header with your secret key",
          "Test your Zap and turn it on!"
        ]
      };
    } else if (webhook.webhook_type === 'make') {
      return {
        title: "Set up your Make integration",
        steps: [
          "Go to Make.com and create a new scenario",
          "Add your trigger module",
          "Add 'HTTP' module → Select 'Make a request'",
          "Configure the HTTP request:",
          "  • Method: POST",
          "  • URL: Paste your webhook URL (from below)",
          "  • Headers: Add X-Webhook-Secret with your secret key",
          "  • Body: Add your contact fields as JSON",
          "Test your scenario and activate it!"
        ]
      };
    } else {
      return {
        title: "Set up your custom webhook integration",
        steps: [
          "Configure your system to send HTTP POST requests",
          "Use the webhook URL and secret key below",
          "Send data in JSON format",
          "Include X-Webhook-Secret header with your secret key",
          "Test the connection"
        ]
      };
    }
  };

  const getTestData = () => {
    if (testData) {
      return testData;
    }
    
    // Generate test data based on custom mapping
    // custom_mapping keys are source field names (what users enter in Zapier)
    const mapping = webhook.custom_mapping || {};
    const sampleData: any = {};
    
    // If mapping is empty, use default fields
    if (!Object.keys(mapping).length) {
      sampleData.email = "customer@example.com";
      sampleData.first_name = "Jane";
      sampleData.last_name = "Smith";
      sampleData.phone = "+1234567890";
    } else {
      // Add all source fields from mapping with example values
      // Use source field names (keys) - these are what users need to enter in Zapier
      Object.keys(mapping).forEach(sourceField => {
        // Generate example value based on source field name
        const lowerField = sourceField.toLowerCase();
        if (lowerField.includes('email')) {
          sampleData[sourceField] = "customer@example.com";
        } else if (lowerField.includes('phone')) {
          sampleData[sourceField] = "+1234567890";
        } else if (lowerField.includes('first')) {
          sampleData[sourceField] = "Jane";
        } else if (lowerField.includes('last')) {
          sampleData[sourceField] = "Smith";
        } else if (lowerField.includes('date')) {
          sampleData[sourceField] = "2024-01-15";
        } else if (lowerField.includes('name') && !lowerField.includes('first') && !lowerField.includes('last')) {
          sampleData[sourceField] = "Example Name";
        } else if (lowerField.includes('id')) {
          sampleData[sourceField] = "ID123456";
        } else if (lowerField.includes('amount') || lowerField.includes('price') || lowerField.includes('total')) {
          sampleData[sourceField] = "100.00";
        } else if (lowerField.includes('type') || lowerField.includes('service')) {
          sampleData[sourceField] = "Example Service";
        } else if (lowerField.includes('provider') || lowerField.includes('doctor') || lowerField.includes('stylist')) {
          sampleData[sourceField] = "Dr. Example";
        } else if (lowerField.includes('insurance')) {
          sampleData[sourceField] = "Example Insurance";
        } else if (lowerField.includes('company')) {
          sampleData[sourceField] = "Example Corp";
        } else if (lowerField.includes('notes') || lowerField.includes('note')) {
          sampleData[sourceField] = "Example notes";
        } else {
          sampleData[sourceField] = `example_${sourceField}`;
        }
      });
    }
    
    return { data: sampleData };
  };

  const setupInstructions = getSetupInstructions();
  const testDataJson = getTestData();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (!isExpanded) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-blue-900">Zapier Setup Instructions</span>
            <span className="text-sm text-blue-600">(Click to expand)</span>
          </div>
          <ChevronDown className="h-5 w-5 text-blue-600" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 mb-6">
      {/* Header with Toggle */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <button
          onClick={() => setIsExpanded(false)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-blue-900">Zapier Setup Instructions</span>
            <span className="text-sm text-blue-600">(Click to hide)</span>
          </div>
          <ChevronUp className="h-5 w-5 text-blue-600" />
        </button>
      </div>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
            {setupInstructions.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2">
            {setupInstructions.steps.map((step: string, index: number) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-blue-600 font-semibold">{index + 1}.</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {/* Test Data */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
            Test Data (JSON)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <textarea
              value={JSON.stringify(testDataJson, null, 2)}
              readOnly
              rows={8}
              className="flex-1 px-3 py-2 bg-gray-50 border rounded-md font-mono text-sm"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => copyToClipboard(JSON.stringify(testDataJson, null, 2))}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Zapier Configuration Guide */}
      {webhook.webhook_type === 'zapier' && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="bg-yellow-100 text-yellow-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">!</span>
              Detailed Zapier Configuration Guide
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-yellow-800 font-medium mb-4">
              Follow these exact steps in Zapier's webhook configuration page:
            </p>
            
            <div className="space-y-3 text-sm">
              <div className="bg-white p-3 rounded border border-yellow-300">
                <p className="font-semibold text-gray-900 mb-1">1. Payload Type</p>
                <p className="text-gray-700">Select <strong>"Json"</strong> from the dropdown (this is required - it's not pre-selected!)</p>
              </div>
              
              <div className="bg-white p-3 rounded border border-yellow-300">
                <p className="font-semibold text-gray-900 mb-1">2. URL</p>
                <p className="text-gray-700">Paste your webhook URL (shown in the Webhook URL section below) into the URL field</p>
              </div>
              
              <div className="bg-white p-3 rounded border border-yellow-300">
                <p className="font-semibold text-gray-900 mb-1">3. Data</p>
                <p className="text-gray-700 mb-3">
                  <strong>Add your fields using the key-value method:</strong>
                </p>
                
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <p className="font-semibold text-green-900 text-xs mb-2">✅ Easy Way - Add Fields Directly</p>
                  <p className="text-green-800 text-xs mb-2">
                    Click "Add value set" and add your fields one by one:
                  </p>
                  <ul className="list-disc list-inside text-green-800 text-xs space-y-1 ml-2 mb-2">
                    <li><strong>Key:</strong> <code className="bg-green-100 px-1 rounded">first_name</code></li>
                    <li><strong>Value:</strong> Use Zapier's field picker (click {"{{"}) to select from your trigger, e.g., <code className="bg-green-100 px-1 rounded">{"{{firstname from HubSpot}}"}</code></li>
                  </ul>
                  <p className="text-green-800 text-xs mb-2">
                    Repeat for each field you need: <code className="bg-green-100 px-1 rounded">email</code>, <code className="bg-green-100 px-1 rounded">phone</code>, <code className="bg-green-100 px-1 rounded">last_name</code>, etc.
                  </p>
                  <p className="text-green-700 text-xs font-semibold">
                    ✨ That's it! Our system automatically handles the formatting.
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-3 rounded border border-yellow-300">
                <p className="font-semibold text-gray-900 mb-1">4. Headers</p>
                <p className="text-gray-700 mb-2">Click "Add value set" and add:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
                  <li><strong>Header Name:</strong> X-Webhook-Secret</li>
                  <li><strong>Header Value:</strong> {webhook.secret_key}</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border border-yellow-300">
                <p className="font-semibold text-gray-900 mb-1">5. Other Fields (Leave as default)</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
                  <li><strong>Wrap Request In Array:</strong> No</li>
                  <li><strong>Basic Auth:</strong> Leave empty</li>
                  <li><strong>Unflatten:</strong> No</li>
                  <li><strong>File:</strong> Leave empty</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ZapierSetupGuide;
